import { httpClient } from '../http-client';
import { buildQueryString } from './query-builder';
import { ApiResponse } from '../../types/responses/api-response';

// ===== SPEAKER API TYPES =====

export interface SpeakerPresentation {
  id: string;
  title: string;
  eventId: string;
  eventTitle: string;
  date: string;
  duration: number;
  status: 'scheduled' | 'completed' | 'cancelled';
  materials: PresentationMaterial[];
  attendees: number;
  rating?: number;
}

export interface PresentationMaterial {
  id: string;
  name: string;
  type: string;
  url: string;
  size: number;
  uploadedAt: string;
  downloads: number;
}

export interface SpeakerSchedule {
  date: string;
  presentations: Array<{
    id: string;
    title: string;
    time: string;
    duration: number;
    location: string;
    status: string;
  }>;
}

export interface SpeakerProfile {
  id: string;
  userId: string;
  bio: string;
  expertise: string[];
  languages: string[];
  presentations: number;
  rating: number;
  totalAttendees: number;
}

export interface SpeakerMobileDashboard {
  upcomingPresentations: SpeakerPresentation[];
  recentMaterials: PresentationMaterial[];
  stats: {
    totalPresentations: number;
    upcomingCount: number;
    totalMaterials: number;
    averageRating: number;
  };
}

// Query параметры
export interface PresentationsQuery {
  page?: number;
  limit?: number;
  status?: 'scheduled' | 'completed' | 'cancelled';
  eventId?: string;
}

export interface MaterialsQuery {
  page?: number;
  limit?: number;
  type?: string;
  presentationId?: string;
}

export interface ScheduleQuery {
  startDate?: string;
  endDate?: string;
}

export interface CreateMaterialData {
  name: string;
  type: string;
  presentationId: string;
  description?: string;
}

export interface UpdateMaterialData {
  name?: string;
  description?: string;
}

export interface UpdateProfileData {
  bio?: string;
  expertise?: string[];
  languages?: string[];
  contactEmail?: string;
  website?: string;
}

// ===== SPEAKER API METHODS =====

export const speakerApi = {
  // ==================== ОСНОВНЫЕ ЭНДПОИНТЫ ====================

  /**
   * Получить список выступлений
   */
  getPresentations: async (query?: PresentationsQuery): Promise<ApiResponse<{
    presentations: SpeakerPresentation[];
    total: number;
    page: number;
    limit: number;
  }>> => {
    return httpClient.get(`/speaker/presentations${buildQueryString(query || {})}`);
  },

  /**
   * Получить расписание
   */
  getSchedule: async (query?: ScheduleQuery): Promise<ApiResponse<{
    schedule: SpeakerSchedule[];
    total: number;
  }>> => {
    return httpClient.get(`/speaker/schedule${buildQueryString(query || {})}`);
  },

  /**
   * Получить материалы
   */
  getMaterials: async (query?: MaterialsQuery): Promise<ApiResponse<{
    materials: PresentationMaterial[];
    total: number;
    page: number;
    limit: number;
  }>> => {
    return httpClient.get(`/speaker/materials${buildQueryString(query || {})}`);
  },

  /**
   * Создать материал
   */
  createMaterial: async (data: CreateMaterialData): Promise<ApiResponse<PresentationMaterial>> => {
    return httpClient.post<PresentationMaterial>('/speaker/materials', data);
  },

  /**
   * Обновить материал
   */
  updateMaterial: async (materialId: string, data: UpdateMaterialData): Promise<ApiResponse<PresentationMaterial>> => {
    return httpClient.put<PresentationMaterial>(`/speaker/materials/${materialId}`, data);
  },

  /**
   * Получить профиль спикера
   */
  getProfile: async (): Promise<ApiResponse<SpeakerProfile>> => {
    return httpClient.get<SpeakerProfile>('/speaker/profile');
  },

  /**
   * Обновить профиль спикера
   */
  updateProfile: async (data: UpdateProfileData): Promise<ApiResponse<SpeakerProfile>> => {
    return httpClient.put<SpeakerProfile>('/speaker/profile', data);
  },

  // ==================== ЗАГРУЗКА ФАЙЛОВ ====================

  /**
   * Загрузить материал (файл)
   * Максимальный размер: 50MB
   * Поддерживаемые типы: PDF, PPTX, DOCX, XLSX, images
   */
  uploadMaterial: async (file: File, presentationId: string, metadata?: {
    name?: string;
    description?: string;
  }): Promise<ApiResponse<PresentationMaterial>> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('presentationId', presentationId);
    if (metadata?.name) formData.append('name', metadata.name);
    if (metadata?.description) formData.append('description', metadata.description);
    
    return httpClient.post<PresentationMaterial>('/speaker/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  // ==================== МОБИЛЬНЫЕ ЭНДПОИНТЫ ====================

  /**
   * Получить мобильную панель спикера
   */
  getMobileDashboard: async (): Promise<ApiResponse<SpeakerMobileDashboard>> => {
    return httpClient.get<SpeakerMobileDashboard>('/speaker/mobile/dashboard');
  },

  /**
   * Получить быстрый список выступлений
   */
  getQuickPresentations: async (limit: number = 5): Promise<ApiResponse<SpeakerPresentation[]>> => {
    return httpClient.get<SpeakerPresentation[]>(`/speaker/mobile/presentations/quick${buildQueryString({ limit })}`);
  },

  /**
   * Получить предстоящие выступления
   */
  getUpcomingSchedule: async (limit: number = 10): Promise<ApiResponse<SpeakerSchedule[]>> => {
    return httpClient.get<SpeakerSchedule[]>(`/speaker/mobile/schedule/upcoming${buildQueryString({ limit })}`);
  },
};

export default speakerApi;

